<route lang="yaml">
meta:
  # The app's own layout (top search + bottom nav), as every other admin page.
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  requiresAdmin: true
</route>

<script setup>
// Admin → Monitoring (Kapil 11/1): "one email when a new issue happens, and
// then the rest in the Monitoring page with enough information to provide to
// you to fix". Intent: is anything broken, and what do I do about it — one
// scan on a phone. Open issues first; each opens to what it means, what to
// fix, the last error and every recent occurrence, and one action: copy a
// summary for Claude Code. Cleared issues from the last day sit below.
import { onMounted, ref } from 'vue'
import axios from '@axios'
import { issueMeta, muteLabel } from '@/utils/monitoring'

const data = ref({ env: '', open: [], recent: [], openCount: 0 })
const loading = ref(false)
const error = ref('')
const expanded = ref(new Set())
const busyKey = ref('')
const snack = ref({ show: false, text: '', color: 'success' })

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await axios.get('/admin/monitoring')
    const body = res?.data?.data
    if (!body || !Array.isArray(body.open))
      throw new Error('unexpected response')
    data.value = body
  } catch (err) {
    error.value = err?.response?.data?.error || 'Monitoring could not be loaded. Try again.'
    data.value = { env: '', open: [], recent: [], openCount: 0 }
  } finally {
    loading.value = false
  }
}

function toggle(key) {
  const next = new Set(expanded.value)
  if (next.has(key))
    next.delete(key)
  else
    next.add(key)
  expanded.value = next
}

async function copyHandoff(issue) {
  try {
    await navigator.clipboard.writeText(issue.handoff)
    snack.value = { show: true, text: 'Copied. Paste it into Claude Code.', color: 'success' }
  } catch (_err) {
    snack.value = { show: true, text: 'Could not copy. Select the summary below and copy it.', color: 'error' }
  }
}

async function mute(issue, hours) {
  busyKey.value = issue.key
  try {
    const res = await axios.post(`/admin/monitoring/${encodeURIComponent(issue.key)}/mute`, { hours })
    const updated = res.data.data
    for (const list of ['open', 'recent']) {
      const i = data.value[list].findIndex(x => x.key === updated.key)
      if (i >= 0)
        data.value[list].splice(i, 1, updated)
    }
    data.value.openCount = data.value.open.filter(x => !x.mutedUntil).length
    snack.value = { show: true, text: hours ? 'Muted for a day. It stays on this page.' : 'Unmuted.', color: 'success' }
  } catch (err) {
    snack.value = { show: true, text: err?.response?.data?.error || 'Could not change the mute. Try again.', color: 'error' }
  } finally {
    busyKey.value = ''
  }
}

onMounted(load)
</script>

<template>
  <div class="mon-page content-wrapper">
    <header class="mon-head">
      <h1 class="mon-title">
        Monitoring
      </h1>
      <p class="mon-sub">
        Problems RM's background checks found<template v-if="data.env">
          on {{ data.env }}
        </template>. You get one email when a new one starts; everything after that is here.
      </p>
    </header>

    <p
      v-if="error"
      class="state state-error"
    >
      {{ error }}
    </p>

    <div
      v-if="loading"
      class="skeleton"
    >
      <div
        v-for="n in 4"
        :key="n"
        class="skeleton-row"
      />
    </div>

    <template v-else-if="!error">
      <section class="group">
        <h2 class="group-label">
          Open
        </h2>
        <p
          v-if="!data.open.length"
          class="state"
        >
          Nothing is wrong right now.
        </p>
        <ul
          v-else
          class="list"
        >
          <li
            v-for="issue in data.open"
            :key="issue.key"
            class="row"
            :class="{ 'row-muted': issue.mutedUntil }"
          >
            <button
              type="button"
              class="row-main"
              :aria-expanded="expanded.has(issue.key)"
              @click="toggle(issue.key)"
            >
              <span class="row-title">{{ issue.title }}</span>
              <span class="meta">{{ issueMeta(issue) }}</span>
              <span
                v-if="issue.mutedUntil"
                class="meta"
              >{{ muteLabel(issue) }}</span>
            </button>
            <div
              v-if="expanded.has(issue.key)"
              class="row-detail"
            >
              <div class="detail-block">
                <div class="detail-label">
                  What it means
                </div>
                <p class="detail-text">
                  {{ issue.what }}
                </p>
              </div>
              <div class="detail-block">
                <div class="detail-label">
                  What to fix
                </div>
                <p class="detail-text">
                  {{ issue.fix }}
                </p>
              </div>
              <div
                v-if="issue.lastError || issue.lastDetail"
                class="detail-block"
              >
                <div class="detail-label">
                  Latest
                </div>
                <p
                  v-if="issue.lastError"
                  class="detail-text detail-error"
                >
                  {{ issue.lastError }}
                </p>
                <p
                  v-if="issue.lastDetail"
                  class="detail-text"
                >
                  {{ issue.lastDetail }}
                </p>
              </div>
              <div class="actions">
                <VBtn
                  color="primary"
                  variant="flat"
                  @click="copyHandoff(issue)"
                >
                  Copy for Claude
                </VBtn>
                <VBtn
                  variant="text"
                  :loading="busyKey === issue.key"
                  @click="mute(issue, issue.mutedUntil ? 0 : 24)"
                >
                  {{ issue.mutedUntil ? 'Unmute' : 'Mute for a day' }}
                </VBtn>
              </div>
              <details class="handoff">
                <summary>Summary and recent occurrences</summary>
                <pre class="detail-text handoff-text">{{ issue.handoff }}</pre>
              </details>
            </div>
          </li>
        </ul>
      </section>

      <section
        v-if="data.recent.length"
        class="group"
      >
        <h2 class="group-label">
          Cleared in the last day
        </h2>
        <ul class="list">
          <li
            v-for="issue in data.recent"
            :key="issue.key"
            class="row"
          >
            <button
              type="button"
              class="row-main"
              :aria-expanded="expanded.has(issue.key)"
              @click="toggle(issue.key)"
            >
              <span class="row-title row-title-quiet">{{ issue.title }}</span>
              <span class="meta">{{ issueMeta(issue) }}</span>
            </button>
            <div
              v-if="expanded.has(issue.key)"
              class="row-detail"
            >
              <p
                v-if="issue.lastError"
                class="detail-text detail-error"
              >
                {{ issue.lastError }}
              </p>
              <div class="actions">
                <VBtn
                  variant="text"
                  @click="copyHandoff(issue)"
                >
                  Copy for Claude
                </VBtn>
              </div>
              <details class="handoff">
                <summary>Summary and recent occurrences</summary>
                <pre class="detail-text handoff-text">{{ issue.handoff }}</pre>
              </details>
            </div>
          </li>
        </ul>
      </section>
    </template>

    <VSnackbar
      v-model="snack.show"
      :timeout="2500"
      :color="snack.color"
    >
      {{ snack.text }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.mon-page {
  max-width: 880px;
  padding-top: 24px;
  padding-bottom: 48px;
}

.mon-head {
  margin-bottom: 24px;
}

.mon-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 4px;
}

.mon-sub {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0;
}

.group {
  margin-bottom: 32px;
}

.group-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin: 0 0 8px;
}

.state {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  padding: 16px 0;
  margin: 0;
}

.state-error {
  color: rgb(var(--v-theme-error));
}

.skeleton-row {
  height: 56px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  margin-bottom: 8px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.row:last-child {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.row-main {
  display: block;
  width: 100%;
  min-height: 48px;
  text-align: left;
  background: none;
  border: 0;
  padding: 12px 4px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}

.row-main:hover,
.row-main:focus-visible {
  background: rgba(var(--v-theme-on-surface), 0.03);
  outline: none;
}

.row-main:focus-visible {
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-primary));
  border-radius: 8px;
}

.row-title {
  display: block;
  font-size: 16px;
  line-height: 1.4;
  color: rgb(var(--v-theme-error));
}

.row-muted .row-title,
.row-title-quiet {
  color: inherit;
}

.meta {
  display: block;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-top: 4px;
}

.row-detail {
  padding: 0 4px 16px;
}

.detail-block {
  margin-bottom: 12px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 4px;
}

.detail-text {
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

.detail-error {
  color: rgb(var(--v-theme-error));
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 12px;
}

.handoff summary {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  padding: 8px 0;
}

.handoff-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 8px;
  padding: 12px;
  margin-top: 4px;
}
</style>
